interface PlayerProps {
  isPlaying: boolean;
  /** 0..1 — fraction of the 30-second preview played. Drives the progress arc. */
  progress?: number;
  /** Number of hints revealed so far (1..totalHints). Drives the numbered markers. */
  revealed?: number;
  /** Total hint count. Default 5. */
  totalHints?: number;
  onToggle: () => void;
  /** Outer diameter px. Default 220. */
  size?: number;
  disabled?: boolean;
}

const NAVY_DEEP = "#061434";
const GOLD = "#d4a24a";
const GOLD_HI = "#f1d286";
const GOLD_LO = "#8c6421";

export function Player({
  isPlaying,
  progress = 0,
  revealed = 0,
  totalHints = 5,
  onToggle,
  size = 220,
  disabled = false,
}: PlayerProps) {
  const label = isPlaying ? "עצור" : "נגן";
  const center = size / 2;
  const markerR = size * 0.473; // where the hint markers sit
  const decorRA = size * 0.418;
  const decorRB = size * 0.436;
  const buttonInset = size * 0.11;

  const progressR = size * 0.4;
  const progressCirc = 2 * Math.PI * progressR;
  const dash = Math.max(0, Math.min(1, progress)) * progressCirc;

  return (
    <div
      className="relative mx-auto"
      style={{ width: size, height: size }}
    >
      <svg
        viewBox={`-${center} -${center} ${size} ${size}`}
        width={size}
        height={size}
        className="absolute inset-0"
        aria-hidden="true"
      >
        {/* numbered hint markers */}
        {Array.from({ length: totalHints }).map((_, i) => {
          const a = (i / totalHints) * Math.PI * 2 - Math.PI / 2;
          const cx = Math.cos(a) * markerR;
          const cy = Math.sin(a) * markerR;
          const on = i < revealed;
          return (
            <g key={i}>
              <circle
                cx={cx}
                cy={cy}
                r={size * 0.032}
                fill={on ? GOLD_HI : "transparent"}
                stroke={on ? GOLD_LO : `${GOLD}40`}
                strokeWidth="1"
              />
              {on && (
                <text
                  x={cx}
                  y={cy + size * 0.014}
                  textAnchor="middle"
                  fontSize={size * 0.041}
                  fontFamily="Heebo, sans-serif"
                  fontWeight="700"
                  fill={NAVY_DEEP}
                >
                  {i + 1}
                </text>
              )}
            </g>
          );
        })}

        {/* decorative concentric arcs */}
        <circle
          cx="0"
          cy="0"
          r={decorRA}
          fill="none"
          stroke={`${GOLD}25`}
          strokeWidth="1"
        />
        <circle
          cx="0"
          cy="0"
          r={decorRB}
          fill="none"
          stroke={`${GOLD}18`}
          strokeWidth="0.5"
          strokeDasharray="2 4"
        />

        {/* progress arc (only when there's progress) */}
        {progress > 0 && (
          <circle
            cx="0"
            cy="0"
            r={progressR}
            fill="none"
            stroke={GOLD}
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${progressCirc}`}
            transform="rotate(-90)"
            opacity="0.5"
          />
        )}
      </svg>

      {/* gold play button */}
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        aria-label={label}
        className="absolute rounded-full cursor-pointer disabled:opacity-60"
        style={{
          inset: buttonInset,
          border: 0,
          background: `radial-gradient(circle at 35% 30%, ${GOLD_HI}, ${GOLD} 55%, ${GOLD_LO})`,
          boxShadow: `
            0 12px 30px rgba(0,0,0,0.45),
            inset 0 2px 0 ${GOLD_HI},
            inset 0 -3px 6px ${GOLD_LO}
          `,
          display: "grid",
          placeItems: "center",
        }}
      >
        <div
          style={{
            width: size * 0.627,
            height: size * 0.627,
            borderRadius: "50%",
            background: `radial-gradient(circle at 40% 35%, #e8b75a, ${GOLD} 60%, ${GOLD_LO})`,
            border: `2px solid ${NAVY_DEEP}`,
            display: "grid",
            placeItems: "center",
            position: "relative",
          }}
        >
          {isPlaying ? (
            <PauseIcon size={size * 0.25} />
          ) : (
            <svg
              width={size * 0.263}
              height={size * 0.263}
              viewBox="0 0 24 24"
              fill={NAVY_DEEP}
              style={{ transform: "translateX(2px)" }}
            >
              <path d="M6 4l14 8-14 8z" />
            </svg>
          )}

          {isPlaying && (
            <>
              <div
                style={{
                  position: "absolute",
                  inset: -12,
                  borderRadius: "50%",
                  border: `2px solid ${GOLD_HI}`,
                  animation: "nig-pulse 1.4s ease-out infinite",
                  opacity: 0,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: -24,
                  borderRadius: "50%",
                  border: `1.5px solid ${GOLD}`,
                  animation: "nig-pulse 1.4s ease-out 0.4s infinite",
                  opacity: 0,
                }}
              />
            </>
          )}
        </div>
      </button>

      <style>
        {`@keyframes nig-pulse {
            0% { transform: scale(0.95); opacity: 0.7; }
            100% { transform: scale(1.15); opacity: 0; }
          }`}
      </style>
    </div>
  );
}

function PauseIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={NAVY_DEEP} aria-hidden="true">
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}
